import type {
  AcquisitionStage,
  OutreachTemplate,
  SalesClaim,
  SalesEvent,
  SalesOffer,
  SalesOutreach,
  SalesPurpose,
  SalesRep,
  SalesSubject,
} from '@/client/types/sales';
import { buildMatchKeys, deriveAcquisitionStage, spendBandPart } from '@/client/types/sales';

/** Evaluated once at module load so relative fixtures stay fresh after refresh. */
const T0 = Date.now();

export function hoursAgo(h: number): string {
  return new Date(T0 - h * 3600_000).toISOString();
}

export function hoursFromNow(h: number): string {
  return new Date(T0 + h * 3600_000).toISOString();
}

export function daysAgo(d: number): string {
  return hoursAgo(d * 24);
}

export function daysFromNow(d: number): string {
  return hoursFromNow(d * 24);
}

export const SALES_REPS: SalesRep[] = [
  {
    id: 'rep_naa',
    name: 'Naa Adjei',
    role: 'manager',
    desk: 'all',
    title: 'Head of sales',
    territory: { regions: ['Accra', 'Kumasi'], verticals: ['homes', 'market', 'locals', 'places'] },
    avatarColor: 'bg-violet-500',
  },
  {
    id: 'rep_ama',
    name: 'Ama Boateng',
    role: 'lead',
    desk: 'homes',
    title: 'Homes lead · Accra',
    territory: { regions: ['Accra', 'East Legon', 'Osu'], verticals: ['homes'] },
    quotaGhs: 80_000,
    avatarColor: 'bg-amber-500',
  },
  {
    id: 'rep_yaa',
    name: 'Yaa Asantewaa',
    role: 'agent',
    desk: 'homes',
    title: 'Homes · Tema / East',
    territory: { regions: ['East Legon', 'Tema', 'Osu'], verticals: ['homes'] },
    quotaGhs: 70_000,
    avatarColor: 'bg-orange-500',
  },
  {
    id: 'rep_kwaku',
    name: 'Kwaku Boateng',
    role: 'agent',
    desk: 'homes',
    title: 'Homes · Kumasi',
    territory: { regions: ['Kumasi'], verticals: ['homes'] },
    quotaGhs: 55_000,
    avatarColor: 'bg-yellow-600',
  },
  {
    id: 'rep_kojo',
    name: 'Kojo Mensah',
    role: 'lead',
    desk: 'market',
    title: 'Market lead · Kumasi',
    territory: { regions: ['Kumasi', 'Adum'], verticals: ['market'] },
    quotaGhs: 50_000,
    avatarColor: 'bg-sky-500',
  },
  {
    id: 'rep_akosua',
    name: 'Akosua Darko',
    role: 'agent',
    desk: 'market',
    title: 'Market · Accra',
    territory: { regions: ['Accra', 'Osu', 'Tema'], verticals: ['market'] },
    quotaGhs: 55_000,
    avatarColor: 'bg-cyan-600',
  },
  {
    id: 'rep_fiifi',
    name: 'Fiifi Annan',
    role: 'agent',
    desk: 'market',
    title: 'Market · autos',
    territory: { regions: ['Accra', 'Kumasi'], verticals: ['market'] },
    quotaGhs: 45_000,
    avatarColor: 'bg-teal-600',
  },
  {
    id: 'rep_esi',
    name: 'Esi Lamptey',
    role: 'lead',
    desk: 'locals',
    title: 'Locals lead · Accra',
    territory: { regions: ['Accra', 'Osu', 'East Legon'], verticals: ['locals'] },
    quotaGhs: 40_000,
    avatarColor: 'bg-emerald-600',
  },
  {
    id: 'rep_kweku',
    name: 'Kweku Sarpong',
    role: 'agent',
    desk: 'locals',
    title: 'Locals · Kumasi',
    territory: { regions: ['Kumasi', 'Adum'], verticals: ['locals'] },
    quotaGhs: 35_000,
    avatarColor: 'bg-lime-700',
  },
  {
    id: 'rep_abla',
    name: 'Abla Nyarko',
    role: 'lead',
    desk: 'places',
    title: 'Places lead · Accra',
    territory: { regions: ['Accra', 'Osu', 'East Legon'], verticals: ['places'] },
    quotaGhs: 40_000,
    avatarColor: 'bg-rose-500',
  },
];

export const SALES_PURPOSES: SalesPurpose[] = [
  {
    id: 'pur_signup',
    name: 'Acquire FeelyFeely account',
    goal: 'signup',
    targetOutcome: 'account_created',
    defaultClaimDays: 7,
    estimatedValueGhs: 400,
    nativeMetric: { value: 1, unit: 'account' },
    active: true,
  },
  {
    id: 'pur_homes',
    name: 'First home listing',
    goal: 'supply',
    vertical: 'homes',
    targetOutcome: 'first_listing',
    defaultClaimDays: 14,
    estimatedValueGhs: 12_000,
    nativeMetric: { value: 3, unit: 'listings' },
    active: true,
  },
  {
    id: 'pur_market',
    name: 'Dealer inventory',
    goal: 'supply',
    vertical: 'market',
    targetOutcome: 'first_listing',
    defaultClaimDays: 14,
    estimatedValueGhs: 8_000,
    nativeMetric: { value: 10, unit: 'listings' },
    active: true,
  },
  {
    id: 'pur_locals',
    name: 'First locals listing',
    goal: 'supply',
    vertical: 'locals',
    targetOutcome: 'first_listing',
    defaultClaimDays: 14,
    estimatedValueGhs: 4_000,
    nativeMetric: { value: 1, unit: 'listing' },
    active: true,
  },
  {
    id: 'pur_places',
    name: 'First places listing',
    goal: 'supply',
    vertical: 'places',
    targetOutcome: 'first_listing',
    defaultClaimDays: 14,
    estimatedValueGhs: 5_000,
    nativeMetric: { value: 1, unit: 'listing' },
    active: true,
  },
  {
    id: 'pur_reactivate',
    name: 'Reactivate dormant lister',
    goal: 'supply',
    targetOutcome: 'listing_reactivated',
    defaultClaimDays: 10,
    estimatedValueGhs: 6_000,
    nativeMetric: { value: 2, unit: 'listings' },
    active: true,
  },
  {
    id: 'pur_org',
    name: 'Stand up org and list',
    goal: 'supply',
    targetOutcome: 'org_created',
    defaultClaimDays: 21,
    estimatedValueGhs: 18_000,
    nativeMetric: { value: 1, unit: 'org' },
    active: true,
  },
  {
    id: 'pur_featured',
    name: 'First featured week',
    goal: 'monetize',
    targetOutcome: 'first_promotion',
    defaultClaimDays: 10,
    estimatedValueGhs: 2_400,
    nativeMetric: { value: 200, unit: 'GHS/mo' },
    active: true,
  },
];

export const SALES_OFFERS: SalesOffer[] = [
  {
    id: 'off_voucher',
    name: 'GHS 50 listing voucher',
    kind: 'voucher',
    costGhs: 50,
    capPerClaim: 1,
    active: true,
    modulePath: '/admin/vouchers',
  },
  {
    id: 'off_credit',
    name: 'GHS 200 promotion credit',
    kind: 'promotion_credit',
    costGhs: 200,
    capPerClaim: 1,
    active: true,
    modulePath: '/admin/promotions',
  },
  {
    id: 'off_featured',
    name: 'Free featured week',
    kind: 'featured_week',
    costGhs: 180,
    capPerClaim: 1,
    active: true,
    modulePath: '/admin/promotions',
  },
  {
    id: 'off_badge',
    name: 'Verified-badge fast-track',
    kind: 'badge_fast_track',
    costGhs: 0,
    capPerClaim: 1,
    active: true,
    modulePath: '/admin/badges',
  },
];

function subject(partial: Omit<SalesSubject, 'matchKeys' | 'fitParts' | 'goalCooldownUntil' | 'createdAt' | 'acquisitionStage' | 'profile'> & {
  matchKeys?: string[];
  fitParts?: SalesSubject['fitParts'];
  goalCooldownUntil?: SalesSubject['goalCooldownUntil'];
  jijiUserId?: string | number;
  meqasaAgentKey?: string;
  acquisitionStage?: AcquisitionStage;
  profile?: SalesSubject['profile'];
  pooledAt?: string;
}): SalesSubject {
  const matchKeys =
    partial.matchKeys ??
    buildMatchKeys({
      phone: partial.phone,
      email: partial.email,
      uid: partial.platform?.uid,
      oid: partial.platform?.oid,
      jijiUserId: partial.jijiUserId,
      meqasaAgentKey: partial.meqasaAgentKey,
    });
  const { jijiUserId: _j, meqasaAgentKey: _m, acquisitionStage: stageHint, profile: profileHint, pooledAt: pooledHint, ...rest } =
    partial;
  const research = stageHint === 'research';
  const pooledAt = pooledHint ?? (research ? undefined : daysAgo(36));
  const draft: SalesSubject = {
    ...rest,
    matchKeys,
    fitParts: partial.fitParts ?? {
      areaDemand: 0.6,
      activityRecency: 0.6,
      spendBand: spendBandPart(partial.spendBand),
    },
    goalCooldownUntil: partial.goalCooldownUntil ?? {},
    createdAt: daysAgo(40),
    pooledAt,
    acquisitionStage: stageHint ?? 'prospect',
    profile: profileHint ?? {
      autoFilled: ['displayName', 'phone', 'areas', 'verticals', 'spendBand'],
      lastEnrichedAt: daysAgo(2),
      manualOverrides: [],
    },
  };
  return { ...draft, acquisitionStage: stageHint ?? deriveAcquisitionStage(draft) };
}

export const SALES_SUBJECTS: SalesSubject[] = [
  subject({
    id: 'sub_kwame',
    kind: 'person',
    displayName: 'Kwame Asante',
    phone: '0244111222',
    email: 'kwame.asante@gmail.com',
    areas: [{ lid: 101, region: 'East Legon' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 14, lastSeen: hoursAgo(18), inventoryValueGhs: 2_400_000, paidOnSource: true, premium: true },
    source: { system: 'ff-prospect', collection: 'meqasa.agent', externalId: 'g:4411' },
    consent: { whatsapp: true, phone: true, email: true },
    doNotContact: false,
    meqasaAgentKey: 'g:4411',
    fitParts: { areaDemand: 0.92, activityRecency: 0.88, spendBand: 1 },
  }),
  subject({
    id: 'sub_efua',
    kind: 'person',
    displayName: 'Efua Mensah',
    phone: '+233 20 555 0101',
    email: 'efua@example.com',
    areas: [{ lid: 102, region: 'Osu' }],
    verticals: ['homes'],
    spendBand: 'mid',
    activity: { listingCount: 3, lastSeen: daysAgo(4), inventoryValueGhs: 180_000 },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '8821' },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 8821,
    fitParts: { areaDemand: 0.7, activityRecency: 0.55, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_yaw_dup',
    kind: 'person',
    displayName: 'Yaw Darko',
    phone: '0277000999',
    areas: [{ lid: 101, region: 'Accra' }],
    verticals: ['homes'],
    spendBand: 'mid',
    activity: { listingCount: 2, lastSeen: daysAgo(2) },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '9901' },
    consent: { phone: true },
    doNotContact: false,
    jijiUserId: 9901,
    fitParts: { areaDemand: 0.65, activityRecency: 0.7, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_yaw_meqasa',
    kind: 'business',
    displayName: 'Yaw Darko Realty',
    phone: '+233277000999',
    email: 'yaw@darkorealty.com',
    areas: [{ lid: 101, region: 'East Legon' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 22, lastSeen: hoursAgo(6), inventoryValueGhs: 5_100_000, paidOnSource: true },
    source: { system: 'ff-prospect', collection: 'meqasa.agent', externalId: 'g:9901' },
    consent: { whatsapp: true, email: true, phone: true },
    doNotContact: false,
    meqasaAgentKey: 'g:9901',
    fitParts: { areaDemand: 0.9, activityRecency: 0.95, spendBand: 1 },
  }),
  subject({
    id: 'sub_adwoa',
    kind: 'person',
    displayName: 'Adwoa Boateng',
    phone: '0551234000',
    email: 'adwoa.b@gmail.com',
    areas: [{ lid: 101, region: 'Accra' }],
    verticals: ['homes'],
    spendBand: 'low',
    activity: { listingCount: 0, lastSeen: daysAgo(20) },
    platform: { uid: 44021, listingCount: 0, lastActiveAt: daysAgo(12), lifetimePaidGhs: 0 },
    source: { system: 'ff-prospect', collection: 'jiji.user', externalId: '44021' },
    consent: { whatsapp: true, email: true, sms: true },
    doNotContact: false,
    fitParts: { areaDemand: 0.8, activityRecency: 0.35, spendBand: 0.4 },
  }),
  subject({
    id: 'sub_kofi_lister',
    kind: 'person',
    displayName: 'Kofi Owusu',
    phone: '0248888111',
    areas: [{ lid: 101, region: 'Accra' }],
    verticals: ['homes'],
    spendBand: 'mid',
    activity: { listingCount: 6, lastSeen: hoursAgo(40), inventoryValueGhs: 420_000, listingCategories: ['Houses', 'Apartments'] },
    platform: { uid: 55102, listingCount: 12, lastActiveAt: hoursAgo(6), lifetimePaidGhs: 6_070 },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '55102' },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 55102,
    fitParts: { areaDemand: 0.75, activityRecency: 0.6, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_org_accra',
    kind: 'organization',
    displayName: 'Legon Homes Ltd',
    email: 'ops@legonhomes.com',
    phone: '0302667788',
    areas: [{ lid: 101, region: 'East Legon' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 0, lastSeen: daysAgo(8) },
    platform: { oid: 901, listingCount: 0, lastActiveAt: daysAgo(8), lifetimePaidGhs: 0 },
    source: { system: 'manual' },
    consent: { email: true, phone: true },
    doNotContact: false,
    fitParts: { areaDemand: 0.85, activityRecency: 0.4, spendBand: 1 },
  }),
  subject({
    id: 'sub_serwaa',
    kind: 'business',
    displayName: 'Serwaa Motors',
    phone: '0247000555',
    areas: [{ lid: 201, region: 'Kumasi' }],
    verticals: ['market'],
    spendBand: 'mid',
    activity: { listingCount: 18, lastSeen: hoursAgo(10), inventoryValueGhs: 310_000, paidOnSource: true },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '7702' },
    platform: { uid: 77021, listingCount: 4, lastActiveAt: hoursAgo(10), lifetimePaidGhs: 0 },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 7702,
    fitParts: { areaDemand: 0.7, activityRecency: 0.85, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_cooling',
    kind: 'person',
    displayName: 'Akosua Frimpong',
    phone: '0201111333',
    areas: [{ lid: 102, region: 'Osu' }],
    verticals: ['homes'],
    spendBand: 'mid',
    activity: { listingCount: 1, lastSeen: daysAgo(15) },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '3301' },
    consent: { phone: true },
    doNotContact: false,
    subjectCooldownUntil: daysFromNow(12),
    jijiUserId: 3301,
    fitParts: { areaDemand: 0.6, activityRecency: 0.2, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_dnc',
    kind: 'person',
    displayName: 'Nana Yeboah',
    phone: '0249999000',
    areas: [{ lid: 101, region: 'Accra' }],
    verticals: ['homes'],
    spendBand: 'low',
    activity: { listingCount: 0 },
    source: { system: 'csv' },
    consent: { whatsapp: false, sms: false, phone: false, email: false },
    doNotContact: true,
    fitParts: { areaDemand: 0.5, activityRecency: 0.1, spendBand: 0.4 },
  }),
  subject({
    id: 'sub_cap',
    kind: 'business',
    displayName: 'Adum Phones',
    phone: '0243333444',
    areas: [{ lid: 201, region: 'Adum' }],
    verticals: ['market'],
    spendBand: 'low',
    activity: { listingCount: 40, lastSeen: hoursAgo(5), inventoryValueGhs: 48_000 },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '1204' },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 1204,
    fitParts: { areaDemand: 0.55, activityRecency: 0.9, spendBand: 0.4 },
  }),
  subject({
    id: 'sub_won_signup',
    kind: 'person',
    displayName: 'Abena Sarpong',
    phone: '0552000111',
    email: 'abena.s@gmail.com',
    areas: [{ lid: 101, region: 'Accra' }],
    verticals: ['homes'],
    spendBand: 'mid',
    activity: { listingCount: 0, lastSeen: daysAgo(3) },
    platform: { uid: 88200, listingCount: 0, lastActiveAt: daysAgo(2), lifetimePaidGhs: 0 },
    source: { system: 'ff-prospect', collection: 'jiji.user', externalId: '88200' },
    consent: { whatsapp: true, email: true, phone: true },
    doNotContact: false,
    goalCooldownUntil: { signup: daysFromNow(80) },
    fitParts: { areaDemand: 0.72, activityRecency: 0.5, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_lost_price',
    kind: 'business',
    displayName: 'Tema Spare Parts',
    phone: '0241212121',
    areas: [{ lid: 201, region: 'Kumasi' }],
    verticals: ['market'],
    spendBand: 'low',
    activity: { listingCount: 9, lastSeen: daysAgo(6) },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '2109' },
    consent: { phone: true },
    doNotContact: false,
    subjectCooldownUntil: daysFromNow(18),
    jijiUserId: 2109,
    fitParts: { areaDemand: 0.4, activityRecency: 0.45, spendBand: 0.4 },
  }),
  subject({
    id: 'sub_place',
    kind: 'business',
    displayName: 'Labadi Beach Grill',
    phone: '0302556677',
    areas: [{ lid: 103, region: 'Accra' }],
    verticals: ['places'],
    spendBand: 'mid',
    activity: { listingCount: 1, lastSeen: daysAgo(1) },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '4400' },
    consent: { whatsapp: true, email: true },
    doNotContact: false,
    jijiUserId: 4400,
    fitParts: { areaDemand: 0.5, activityRecency: 0.75, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_osu_bar',
    kind: 'business',
    displayName: 'Osu Rooftop',
    phone: '0246111222',
    email: 'book@osurooftop.com',
    areas: [{ lid: 102, region: 'Osu' }],
    verticals: ['places'],
    spendBand: 'high',
    activity: { listingCount: 2, lastSeen: hoursAgo(22), inventoryValueGhs: 0, paidOnSource: true },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '4412' },
    consent: { whatsapp: true, email: true, phone: true },
    doNotContact: false,
    jijiUserId: 4412,
    fitParts: { areaDemand: 0.78, activityRecency: 0.7, spendBand: 1 },
  }),
  subject({
    id: 'sub_kumasi_lodge',
    kind: 'business',
    displayName: 'Adum Guest House',
    phone: '0242000888',
    areas: [{ lid: 201, region: 'Adum' }],
    verticals: ['places'],
    spendBand: 'mid',
    activity: { listingCount: 1, lastSeen: daysAgo(9) },
    source: { system: 'manual' },
    consent: { phone: true, email: true },
    doNotContact: false,
    fitParts: { areaDemand: 0.45, activityRecency: 0.4, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_salon',
    kind: 'business',
    displayName: 'Esi Cuts East Legon',
    phone: '0245111333',
    areas: [{ lid: 101, region: 'East Legon' }],
    verticals: ['locals'],
    spendBand: 'mid',
    activity: { listingCount: 4, lastSeen: hoursAgo(12) },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '5520' },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 5520,
    fitParts: { areaDemand: 0.82, activityRecency: 0.8, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_tutor',
    kind: 'person',
    displayName: 'Kwame Tutors',
    phone: '0553000444',
    email: 'kwame.tutors@gmail.com',
    areas: [{ lid: 102, region: 'Osu' }],
    verticals: ['locals'],
    spendBand: 'low',
    activity: { listingCount: 2, lastSeen: daysAgo(3) },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '5521' },
    consent: { whatsapp: true, email: true },
    doNotContact: false,
    jijiUserId: 5521,
    fitParts: { areaDemand: 0.6, activityRecency: 0.65, spendBand: 0.4 },
  }),
  subject({
    id: 'sub_pharmacy',
    kind: 'business',
    displayName: 'Adum Meds',
    phone: '0322022111',
    areas: [{ lid: 201, region: 'Adum' }],
    verticals: ['locals'],
    spendBand: 'mid',
    activity: { listingCount: 0, lastSeen: daysAgo(5) },
    source: { system: 'csv' },
    consent: { phone: true, whatsapp: true },
    doNotContact: false,
    fitParts: { areaDemand: 0.58, activityRecency: 0.5, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_kumasi_homes',
    kind: 'person',
    displayName: 'Akua Mensah Realty',
    phone: '0248000777',
    email: 'akua.m@realty.gh',
    areas: [{ lid: 201, region: 'Kumasi' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 11, lastSeen: hoursAgo(8), inventoryValueGhs: 1_800_000, paidOnSource: true },
    source: { system: 'ff-prospect', collection: 'meqasa.agent', externalId: 'g:8801' },
    consent: { whatsapp: true, phone: true, email: true },
    doNotContact: false,
    meqasaAgentKey: 'g:8801',
    fitParts: { areaDemand: 0.74, activityRecency: 0.86, spendBand: 1 },
  }),
  subject({
    id: 'sub_accra_phones',
    kind: 'business',
    displayName: 'Circle Gadgets',
    phone: '0241222333',
    areas: [{ lid: 101, region: 'Accra' }],
    verticals: ['market'],
    spendBand: 'mid',
    activity: { listingCount: 32, lastSeen: hoursAgo(4), inventoryValueGhs: 96_000, paidOnSource: true },
    source: { system: 'ff-prospect', collection: 'jiji.seller', externalId: '6610' },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 6610,
    fitParts: { areaDemand: 0.68, activityRecency: 0.92, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_csv_dealer',
    kind: 'business',
    displayName: 'Tema Auto Yard',
    phone: '0243444555',
    email: 'sales@temaauto.gh',
    areas: [{ lid: 104, region: 'Tema' }],
    verticals: ['market'],
    spendBand: 'high',
    activity: { listingCount: 28, lastSeen: daysAgo(2), inventoryValueGhs: 2_200_000 },
    source: { system: 'csv' },
    consent: { phone: true, email: true, whatsapp: true },
    doNotContact: false,
    fitParts: { areaDemand: 0.66, activityRecency: 0.6, spendBand: 1 },
  }),
  subject({
    id: 'sub_my_agent',
    kind: 'person',
    displayName: 'Kojo Asiedu',
    phone: '0247000888',
    email: 'kojo.asiedu@asieduhomes.gh',
    areas: [{ lid: 101, region: 'East Legon' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 9, lastSeen: hoursAgo(3), inventoryValueGhs: 3_200_000, paidOnSource: true, listingCategories: ['Houses', 'Land'] },
    platform: { uid: 91001, listingCount: 9, lastActiveAt: hoursAgo(3), lifetimePaidGhs: 4_250 },
    source: { system: 'manual' },
    consent: { whatsapp: true, phone: true, email: true },
    doNotContact: false,
    addedById: 'rep_ama',
    pooledAt: daysAgo(50),
    profile: {
      notes: 'Ama’s own agent. Brought in from her book, now listing and paying.',
      autoFilled: [],
      lastEnrichedAt: daysAgo(1),
      manualOverrides: ['notes', 'displayName'],
    },
    fitParts: { areaDemand: 0.9, activityRecency: 0.93, spendBand: 1 },
  }),
  subject({
    id: 'sub_research_meqasa',
    kind: 'person',
    displayName: 'Afia Owusu',
    phone: '0246111999',
    email: 'afia.owusu@homes-agent.gh',
    areas: [{ lid: 101, region: 'East Legon' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 31, lastSeen: hoursAgo(9), inventoryValueGhs: 6_400_000, paidOnSource: true, premium: true },
    source: { system: 'research', collection: 'meqasa.agent', externalId: 'g:1204' },
    consent: { whatsapp: true, phone: true, email: true },
    doNotContact: false,
    meqasaAgentKey: 'g:1204',
    acquisitionStage: 'research',
    fitParts: { areaDemand: 0.94, activityRecency: 0.9, spendBand: 1 },
  }),
  subject({
    id: 'sub_research_jiji',
    kind: 'business',
    displayName: 'Kejetia Phones Hub',
    phone: '0243888000',
    areas: [{ lid: 201, region: 'Adum' }],
    verticals: ['market'],
    spendBand: 'mid',
    activity: { listingCount: 56, lastSeen: hoursAgo(3), inventoryValueGhs: 140_000, paidOnSource: true },
    source: { system: 'research', collection: 'jiji.seller', externalId: '88012' },
    consent: { whatsapp: true, phone: true },
    doNotContact: false,
    jijiUserId: 88012,
    acquisitionStage: 'research',
    fitParts: { areaDemand: 0.72, activityRecency: 0.96, spendBand: 0.7 },
  }),
  subject({
    id: 'sub_research_org',
    kind: 'organization',
    displayName: 'Cantonments Lets Ltd',
    email: 'hello@cantonmentslets.gh',
    phone: '0302445566',
    areas: [{ lid: 105, region: 'Accra' }],
    verticals: ['homes'],
    spendBand: 'high',
    activity: { listingCount: 0, lastSeen: daysAgo(6) },
    source: { system: 'research', collection: 'meqasa.agency', externalId: 'g:agency-44' },
    consent: { email: true, phone: true },
    doNotContact: false,
    meqasaAgentKey: 'g:agency-44',
    acquisitionStage: 'research',
    fitParts: { areaDemand: 0.88, activityRecency: 0.42, spendBand: 1 },
  }),
  subject({
    id: 'sub_research_skipped',
    kind: 'person',
    displayName: 'Yaw Boat Yard',
    phone: '0203333444',
    areas: [{ lid: 104, region: 'Tema' }],
    verticals: ['market'],
    spendBand: 'low',
    activity: { listingCount: 7, lastSeen: daysAgo(11) },
    source: { system: 'research', collection: 'jiji.seller', externalId: '44190' },
    consent: { phone: true },
    doNotContact: false,
    jijiUserId: 44190,
    acquisitionStage: 'research',
    researchCooldownUntil: daysFromNow(9),
    fitParts: { areaDemand: 0.4, activityRecency: 0.3, spendBand: 0.4 },
  }),
];

export const SALES_CLAIMS: SalesClaim[] = [
  {
    id: 'clm_yaw_dup',
    subjectId: 'sub_yaw_dup',
    ownerId: 'rep_ama',
    purposeId: 'pur_homes',
    status: 'claimed',
    claimedAt: hoursAgo(2),
    expiresAt: daysFromNow(12),
  },
  {
    id: 'clm_kwame',
    subjectId: 'sub_kwame',
    ownerId: 'rep_ama',
    purposeId: 'pur_homes',
    status: 'in_pursuit',
    claimedAt: hoursAgo(6),
    expiresAt: daysFromNow(13),
    offerId: 'off_featured',
  },
  {
    id: 'clm_serwaa',
    subjectId: 'sub_serwaa',
    ownerId: 'rep_kojo',
    purposeId: 'pur_market',
    status: 'claimed',
    claimedAt: hoursAgo(30),
    expiresAt: hoursFromNow(-2),
  },
  {
    id: 'clm_cap',
    subjectId: 'sub_cap',
    ownerId: 'rep_kojo',
    purposeId: 'pur_market',
    status: 'in_pursuit',
    claimedAt: daysAgo(3),
    expiresAt: daysFromNow(11),
  },
  {
    id: 'clm_abena_signup',
    subjectId: 'sub_won_signup',
    ownerId: 'rep_ama',
    purposeId: 'pur_signup',
    status: 'won',
    claimedAt: daysAgo(16),
    expiresAt: daysAgo(9),
    win: { goal: 'signup', at: daysAgo(12), uid: 88200 },
  },
  {
    id: 'clm_abena_supply',
    subjectId: 'sub_won_signup',
    ownerId: 'rep_ama',
    purposeId: 'pur_homes',
    status: 'in_pursuit',
    claimedAt: daysAgo(12),
    expiresAt: daysFromNow(2),
    offerId: 'off_voucher',
  },
  {
    id: 'clm_lost',
    subjectId: 'sub_lost_price',
    ownerId: 'rep_kojo',
    purposeId: 'pur_market',
    status: 'lost',
    claimedAt: daysAgo(20),
    expiresAt: daysAgo(6),
    lossReason: 'price',
    lossNote: 'Wanted free featured forever.',
  },
  {
    id: 'clm_kofi_won_supply',
    subjectId: 'sub_kofi_lister',
    ownerId: 'rep_ama',
    purposeId: 'pur_homes',
    status: 'won',
    claimedAt: daysAgo(28),
    expiresAt: daysAgo(14),
    offerId: 'off_badge',
    win: { goal: 'supply', at: daysAgo(18), uid: 55102, listingId: 'lst_kofi_1' },
  },
  {
    id: 'clm_cooling_lost',
    subjectId: 'sub_cooling',
    ownerId: 'rep_ama',
    purposeId: 'pur_signup',
    status: 'lost',
    claimedAt: daysAgo(22),
    expiresAt: daysAgo(15),
    lossReason: 'unreachable',
  },
  {
    id: 'clm_kojo_signup_won',
    subjectId: 'sub_serwaa',
    ownerId: 'rep_kojo',
    purposeId: 'pur_signup',
    status: 'won',
    claimedAt: daysAgo(40),
    expiresAt: daysAgo(33),
    win: { goal: 'signup', at: daysAgo(35), uid: 77021 },
  },
  {
    id: 'clm_efua',
    subjectId: 'sub_efua',
    ownerId: 'rep_yaa',
    purposeId: 'pur_homes',
    status: 'in_pursuit',
    claimedAt: hoursAgo(14),
    expiresAt: daysFromNow(13),
  },
  {
    id: 'clm_kumasi_homes',
    subjectId: 'sub_kumasi_homes',
    ownerId: 'rep_kwaku',
    purposeId: 'pur_homes',
    status: 'claimed',
    claimedAt: hoursAgo(4),
    expiresAt: daysFromNow(13),
  },
  {
    id: 'clm_gadgets',
    subjectId: 'sub_accra_phones',
    ownerId: 'rep_akosua',
    purposeId: 'pur_market',
    status: 'in_pursuit',
    claimedAt: hoursAgo(20),
    expiresAt: daysFromNow(12),
    offerId: 'off_credit',
  },
  {
    id: 'clm_tema_auto',
    subjectId: 'sub_csv_dealer',
    ownerId: 'rep_fiifi',
    purposeId: 'pur_market',
    status: 'claimed',
    claimedAt: hoursAgo(9),
    expiresAt: daysFromNow(13),
  },
  {
    id: 'clm_salon',
    subjectId: 'sub_salon',
    ownerId: 'rep_esi',
    purposeId: 'pur_locals',
    status: 'in_pursuit',
    claimedAt: hoursAgo(11),
    expiresAt: daysFromNow(13),
    offerId: 'off_voucher',
  },
  {
    id: 'clm_pharmacy',
    subjectId: 'sub_pharmacy',
    ownerId: 'rep_kweku',
    purposeId: 'pur_locals',
    status: 'claimed',
    claimedAt: hoursAgo(7),
    expiresAt: daysFromNow(13),
  },
  {
    id: 'clm_place',
    subjectId: 'sub_place',
    ownerId: 'rep_abla',
    purposeId: 'pur_places',
    status: 'committed',
    claimedAt: daysAgo(4),
    expiresAt: daysFromNow(10),
    offerId: 'off_featured',
  },
  {
    id: 'clm_my_agent_signup',
    subjectId: 'sub_my_agent',
    ownerId: 'rep_ama',
    purposeId: 'pur_signup',
    status: 'won',
    claimedAt: daysAgo(48),
    expiresAt: daysAgo(41),
    win: { goal: 'signup', at: daysAgo(45), uid: 91001 },
  },
  {
    id: 'clm_my_agent_supply',
    subjectId: 'sub_my_agent',
    ownerId: 'rep_ama',
    purposeId: 'pur_homes',
    status: 'won',
    claimedAt: daysAgo(45),
    expiresAt: daysAgo(31),
    offerId: 'off_voucher',
    win: { goal: 'supply', at: daysAgo(38), uid: 91001, listingId: 'lst_asiedu_1' },
  },
  {
    id: 'clm_my_agent_monetize',
    subjectId: 'sub_my_agent',
    ownerId: 'rep_ama',
    purposeId: 'pur_featured',
    status: 'won',
    claimedAt: daysAgo(30),
    expiresAt: daysAgo(20),
    offerId: 'off_featured',
    win: { goal: 'monetize', at: daysAgo(22), uid: 91001, promotionId: 'prm_asiedu_1' },
  },
];

export const SALES_OUTREACH_TEMPLATES: OutreachTemplate[] = [
  {
    id: 'tpl_inapp_signup',
    name: 'In-app signup invite',
    channel: 'in_app',
    mode: 'on_platform',
    body: 'Hi {name} — FeelyFeely is the fastest way to get your listings in front of buyers in {area}. Tap the link to finish signup.',
    defaultDest: '/signup',
  },
  {
    id: 'tpl_wa_homes',
    name: 'WhatsApp homes intro',
    channel: 'whatsapp',
    mode: 'off_platform',
    body: 'Hi {name}, Ama from FeelyFeely. We are onboarding agents in {area}. I can get your inventory live this week — including a featured week if you post three homes.',
  },
  {
    id: 'tpl_phone_follow',
    name: 'Phone follow-up',
    channel: 'phone',
    mode: 'off_platform',
    body: 'Called {name} about posting on FeelyFeely. Left a note to send the tracked listing link.',
  },
];

export const SALES_OUTREACHES: SalesOutreach[] = [
  {
    id: 'out_kwame_1',
    subjectId: 'sub_kwame',
    claimId: 'clm_kwame',
    actorId: 'rep_ama',
    channel: 'whatsapp',
    mode: 'off_platform',
    status: 'replied',
    direction: 'outbound',
    body: 'Introduced FeelyFeely homes; sent featured-week offer.',
    templateId: 'tpl_wa_homes',
    trackedLinks: [],
    feedback: { sentiment: 'positive', outcome: 'interested', note: 'Wants to see the posting flow.', at: hoursAgo(4) },
    nextStepDue: hoursFromNow(20),
    at: hoursAgo(5),
  },
  {
    id: 'out_kwame_1_in',
    subjectId: 'sub_kwame',
    claimId: 'clm_kwame',
    actorId: 'rep_ama',
    channel: 'whatsapp',
    mode: 'off_platform',
    status: 'replied',
    direction: 'inbound',
    body: 'Send the posting flow. I have three Tema homes ready this week.',
    trackedLinks: [],
    at: hoursAgo(4),
  },
  {
    id: 'out_abena_1',
    subjectId: 'sub_won_signup',
    claimId: 'clm_abena_supply',
    actorId: 'rep_ama',
    channel: 'in_app',
    mode: 'on_platform',
    status: 'clicked',
    direction: 'outbound',
    body: 'Walked through posting a home listing. Tracked link to create listing.',
    templateId: 'tpl_inapp_signup',
    trackedLinks: [
      {
        token: 'abena-list',
        dest: '/homes/new',
        expiresAt: daysFromNow(2),
        events: [
          { kind: 'open', at: daysAgo(9) },
          { kind: 'click', at: daysAgo(9) },
        ],
      },
    ],
    nextStepDue: hoursFromNow(8),
    at: daysAgo(10),
  },
  {
    id: 'out_abena_1_in',
    subjectId: 'sub_won_signup',
    claimId: 'clm_abena_supply',
    actorId: 'rep_ama',
    channel: 'in_app',
    mode: 'on_platform',
    status: 'replied',
    direction: 'inbound',
    body: 'Listing is up. What is the featured-week offer?',
    trackedLinks: [],
    at: daysAgo(9),
  },
  {
    id: 'out_cap_1',
    subjectId: 'sub_cap',
    claimId: 'clm_cap',
    actorId: 'rep_kojo',
    channel: 'whatsapp',
    mode: 'off_platform',
    status: 'logged',
    direction: 'outbound',
    body: 'Pinged about moving inventory onto FeelyFeely.',
    trackedLinks: [],
    at: daysAgo(2),
  },
  {
    id: 'out_cap_2',
    subjectId: 'sub_cap',
    claimId: 'clm_cap',
    actorId: 'rep_kojo',
    channel: 'whatsapp',
    mode: 'off_platform',
    status: 'logged',
    direction: 'outbound',
    body: 'Follow-up sticker pack. Near channel cap.',
    trackedLinks: [],
    at: hoursAgo(8),
  },
  {
    id: 'out_my_agent_inapp',
    subjectId: 'sub_my_agent',
    claimId: 'clm_my_agent_signup',
    actorId: 'rep_ama',
    channel: 'in_app',
    mode: 'on_platform',
    status: 'replied',
    direction: 'outbound',
    body: 'Sent Kojo the on-platform signup link from Ama’s book.',
    templateId: 'tpl_inapp_signup',
    trackedLinks: [
      {
        token: 'asiedu-join',
        dest: '/signup',
        expiresAt: daysAgo(40),
        events: [
          { kind: 'open', at: daysAgo(46) },
          { kind: 'click', at: daysAgo(46) },
        ],
      },
    ],
    feedback: { sentiment: 'positive', outcome: 'joined', note: 'Created uid 91001 the same day.', at: daysAgo(45) },
    at: daysAgo(47),
  },
  {
    id: 'out_my_agent_in',
    subjectId: 'sub_my_agent',
    claimId: 'clm_my_agent_signup',
    actorId: 'rep_ama',
    channel: 'in_app',
    mode: 'on_platform',
    status: 'replied',
    direction: 'inbound',
    body: 'Signed up. Account is live — send the listing walkthrough.',
    trackedLinks: [],
    at: daysAgo(46),
  },
  {
    id: 'out_place_draft',
    subjectId: 'sub_osu_bar',
    actorId: 'rep_abla',
    channel: 'whatsapp',
    mode: 'off_platform',
    status: 'draft',
    direction: 'outbound',
    body: 'Hi Osu Rooftop — FeelyFeely Places can put you in front of weekend diners. Draft waiting to send.',
    trackedLinks: [],
    at: hoursAgo(3),
  },
  {
    id: 'out_dnc_blocked',
    subjectId: 'sub_dnc',
    actorId: 'rep_yaa',
    channel: 'whatsapp',
    mode: 'off_platform',
    status: 'blocked',
    direction: 'outbound',
    body: 'Tried WhatsApp after CSV import.',
    trackedLinks: [],
    blockedReason: 'Nana Yeboah is marked do-not-contact.',
    at: daysAgo(5),
  },
  {
    id: 'out_kofi_featured',
    subjectId: 'sub_kofi_lister',
    claimId: 'clm_kofi_won_supply',
    actorId: 'rep_ama',
    channel: 'in_app',
    mode: 'on_platform',
    status: 'opened',
    direction: 'outbound',
    body: 'Kofi is live. Sent featured-week deep link.',
    trackedLinks: [
      {
        token: 'kofi-feat',
        dest: '/promotions/featured',
        expiresAt: daysFromNow(1),
        events: [{ kind: 'open', at: daysAgo(16) }],
      },
    ],
    at: daysAgo(17),
  },
];

export const SALES_EVENTS: SalesEvent[] = [
  {
    id: 'evt_kwame_claim',
    at: hoursAgo(6),
    type: 'claim',
    actorId: 'rep_ama',
    subjectId: 'sub_kwame',
    claimId: 'clm_kwame',
    payload: { purposeId: 'pur_homes', offerId: 'off_featured' },
  },
  {
    id: 'evt_kwame_touch',
    at: hoursAgo(5),
    type: 'touch',
    actorId: 'rep_ama',
    subjectId: 'sub_kwame',
    claimId: 'clm_kwame',
    payload: { channel: 'whatsapp' },
  },
  {
    id: 'evt_abena_win',
    at: daysAgo(12),
    type: 'win',
    actorId: 'rep_ama',
    subjectId: 'sub_won_signup',
    claimId: 'clm_abena_signup',
    payload: { goal: 'signup', uid: 88200 },
  },
  {
    id: 'evt_abena_adv',
    at: daysAgo(12),
    type: 'advance',
    actorId: 'rep_ama',
    subjectId: 'sub_won_signup',
    claimId: 'clm_abena_supply',
    payload: { fromClaimId: 'clm_abena_signup', purposeId: 'pur_homes' },
  },
  {
    id: 'evt_lost',
    at: daysAgo(19),
    type: 'lose',
    actorId: 'rep_kojo',
    subjectId: 'sub_lost_price',
    claimId: 'clm_lost',
    payload: { reason: 'price' },
  },
  {
    id: 'evt_kofi_win',
    at: daysAgo(18),
    type: 'win',
    actorId: 'rep_ama',
    subjectId: 'sub_kofi_lister',
    claimId: 'clm_kofi_won_supply',
    payload: { goal: 'supply', listingId: 'lst_kofi_1' },
  },
];
